import React, { useEffect } from 'react';

import { StatusGuard } from 'components/ui/status-guard'
import { CheckboxInputElement } from 'components/ui/checkbox'
import { Button, ButtonGroup } from 'components/ui/button'
import { Modal } from 'components/ui/modal';
import { Description, Image, Title, ListItemLabel, Subtitle } from 'components/ui/list-item-label'
import { sharedActionCreators, SharedActionCreators } from 'containers/shared'
import { connect } from 'containers/store';

import { actionCreators, GettyActionCreators } from 'domains/org/getty/actions';
import { GettyStore } from 'domains/org/getty/store';

import { Site } from 'generated/mos/structure'
import { Tour, TourCategory } from 'generated/mos/org/getty/audio'
import { ListToursRequest } from 'generated/mos/org/getty/audioaccess'
import { Media } from 'generated/mos/media'

import { bffEndpoint } from 'helpers/bff'
import { areArraysOfRefsEqual, pick } from 'helpers/core'
import { Status, statusSelector } from 'helpers/status'
import { space } from 'helpers/style'

import * as entity from 'entity';
import styled from 'styled';

import { categorySchema } from '../../../schemas/category'
import { tourSchema } from '../../../schemas/tour'

const Count = styled.span`
  display: flex;
  flex-direction: row;
  height: 36px;
  margin: 0 0 ${space(2)};
  font-size: ${({ theme }) => theme.font.size.small};
  font-weight: 500;
  text-transform: uppercase;
`

type ConnectedProps =
  Pick<GettyStore['getty'], 'tours' | 'categoryUpdate' | 'sites'>;

type ActionProps =
  Pick<GettyActionCreators, 'gettyListToursRequest' | 'gettyListSitesRequest'> &
  Pick<SharedActionCreators, 'toastNotification'>;

type DirectProps = {
  readonly onClose: () => void;
  readonly category: TourCategory.Entity;
  readonly onSubmit: (categoryRef: TourCategory.Ref, tourRefs: ReadonlyArray<Tour.Ref> ) => void;
};

type Props = ConnectedProps & ActionProps & DirectProps;

const CategoryUpdateToursView = ({
  gettyListToursRequest,
  onClose,
  onSubmit,
  category,
  categoryUpdate,
  sites,
  toastNotification,
  tours,
}: Props) => {

  useEffect(() => {
    gettyListToursRequest({ ...ListToursRequest.defaults });
  }, []) // eslint-disable-line react-hooks/exhaustive-deps


  const toursData = statusSelector.data(tours);
  const currentTourRefs = category.tourRefs ? category.tourRefs : []
  const categoryName = categorySchema.name.read(category.name)
  const sitesData = statusSelector.data(sites)
  const title = `Add / Remove tours from “${categoryName}” category`

  // pre populate tourRefList with current selected tourRefs.
  const tourRefList = entity.useSelectedRefs<Tour.Ref>(currentTourRefs || []);

  useEffect(() => {
    if (categoryUpdate.status === Status.Failed) {
      toastNotification({ type: 'error', text: `There was a problem updating the tours for ${categoryName} category.` })
    }

    if (categoryUpdate.status === Status.Complete) {
      toastNotification({ type: 'success', text: `Tours for ${categoryName} category updated.` })
      onClose()
    }
  }, [categoryName, categoryUpdate, onClose, toastNotification])

  const onSave = () => {
    onSubmit(TourCategory.mustRef(category.ref), tourRefList.selected)
    onClose()
  }

   // resolves the site name based on the ref passsed in.
   const resolveSiteName = (siteRef: Site.Ref | undefined) => {
    if (!siteRef) {
      return ""
    }

    const site = sitesData && sitesData.sites && sitesData.sites.find((site) => Site.mustRef(site.ref).id === siteRef.id)
    return site ? site.name : ""
  }

   /* Enables to pass the comparison check when it has no refs present */
   const currentRefs = currentTourRefs.map((ref => { return { id: ref.id, typename: ref.typename } }))
   const selectedRefs = tourRefList.selected.map((ref => { return { id: ref.id, typename: ref.typename } }))

   const modified = currentRefs && selectedRefs && areArraysOfRefsEqual(currentRefs, selectedRefs)

   const hostName = bffEndpoint(window.location.hostname)

  return (
    <Modal
      onClose={onClose}
      header={<h3>{title}</h3>}
      footer={(
        <ButtonGroup>
          <Button onClick={onClose} appearance="secondary">Cancel</Button>
          <Button onClick={() => onSave()} isDisabled={modified}>Update</Button>
        </ButtonGroup>
      )}
    >
      <StatusGuard status={tours} mode="form">
        {toursData && <Count>{tourRefList.selected.length} of {toursData.length} tours</Count>}
        {toursData && toursData.map(tour => {
            const tourImage = tourSchema.imageRef.read(tour.imageRef) as Media.Ref
            const tourImageId = tourImage && tourImage.id

            const imagePath = tourImageId ? `${hostName}/images/${tourImageId}.jpg?s=crop&h=92&w=92` : ''

            return (
              <ListItemLabel key={tour.ref && tour.ref.id}>
                <CheckboxInputElement
                  checked={tourRefList.includes(tour.ref)}
                  onClick={() => tourRefList.toggle(tour.ref)}
                  />
                <Image src={imagePath} />
                <Description>
                  <Title>{tourSchema.name.read(tour.name)}</Title>
                  <Subtitle>{resolveSiteName(tour.siteRef)}</Subtitle>
                </Description>
              </ListItemLabel>
            )
        })}
      </StatusGuard>
    </Modal>
  )
}

export const CategoryUpdateTours = connect<ConnectedProps, ActionProps, DirectProps, GettyStore>(
    store => pick(store.getty, 'categoryUpdate', 'tours', 'sites'),
    {
      ...pick(actionCreators, 'gettyListToursRequest', 'gettyListSitesRequest'),
      ...pick(sharedActionCreators, 'toastNotification'),
    }
)(CategoryUpdateToursView);
